import React, { useEffect } from 'react';
import Layout from '../components/layout/Layout';
import HeroSection from '../components/shared/HeroSection';
import SectionTitle from '../components/shared/SectionTitle';
import CTASection from '../components/shared/CTASection';
import { FadeInOnScroll } from '../components/shared/Parallax';
import { Play, ExternalLink, Factory, Users, Wrench, Shield, CheckCircle, Phone, Award, HeartHandshake } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const InstallationsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const whyDifferent = [
    {
      icon: Factory,
      title: "Produzione Propria",
      description: "Ogni tenda, pergola e vetrata nasce nel nostro laboratorio a Pisa. Non acquistiamo da terzi: progettiamo e produciamo internamente con la nostra sartoria e il nostro magazzino. Questo significa controllo totale sulla qualità di ogni singolo prodotto.",
      highlights: ["Sartoria interna", "Magazzino proprio", "Controllo qualità al 100%"]
    },
    {
      icon: Users,
      title: "Montatori Interni - Mai Esterni",
      description: "I nostri installatori sono dipendenti dell'azienda, non collaboratori esterni. Conoscono i prodotti perché li vedono nascere ogni giorno. Nessun subappalto, nessuna sorpresa: solo professionisti che lavorano con noi da anni.",
      highlights: ["Dipendenti formati", "Anni di esperienza", "Nessun subappalto"]
    },
    {
      icon: HeartHandshake,
      title: "Assistenza Diretta",
      description: "Quando ci chiami, parli con chi ha prodotto e installato il tuo prodotto. Nessun call center, nessun rimbalzo tra uffici. Un problema? Lo risolviamo noi direttamente, senza intermediari.",
      highlights: ["Contatto diretto", "Risposte immediate", "Nessun intermediario"]
    },
    {
      icon: Shield,
      title: "Garanzia Totale",
      description: "Ci assumiamo la responsabilità completa: dal tessuto alla struttura, dalla produzione all'installazione. Una sola azienda, una sola garanzia. Non dovrai mai chiederti 'chi devo chiamare?'",
      highlights: ["Garanzia unica", "Responsabilità totale", "Assistenza post-vendita"]
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <HeroSection
        title="Le Nostre Installazioni"
        subtitle="Guarda i nostri lavori in video. Ogni progetto è realizzato dai nostri dipendenti esperti, mai da montatori esterni."
        ctaText="Richiedi un preventivo"
        ctaLink="/preventivo"
        imageUrl="https://customer-assets.emergentagent.com/job_tuscany-tende/artifacts/eaeztvq9_Gemini_Generated_Image_5y2iqn5y2iqn5y2i.png"
        showScrollIndicator={true}
      />

      {/* Intro Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                Video Portfolio
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-gray-900 leading-tight mb-6">
                Lavori eseguiti dai nostri esperti
              </h2>
              <p className="text-lg text-gray-600 font-light leading-relaxed mb-8">
                Ogni installazione che vedrai è stata realizzata esclusivamente dai nostri dipendenti. 
                <strong className="text-gray-900"> Non ci affidiamo mai a montatori esterni</strong>: 
                questo ci permette di garantire qualità, precisione e assistenza continua nel tempo.
                Siamo gli unici produttori diretti in Toscana e ci occupiamo di tutto internamente.
              </p>
              <a 
                href="https://youtube.com/@tappezzeriagiovanni7149" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-4 transition-colors duration-300"
              >
                <Play className="w-5 h-5" />
                Visita il nostro canale YouTube
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </FadeInOnScroll>
        </div>
      </section>

      {/* Featured Video Section */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <SectionTitle
              label="Video in Evidenza"
              title="Guarda le installazioni in azione"
              description="Un esempio del nostro lavoro: dalla produzione all'installazione finale."
            />
          </FadeInOnScroll>

          {/* Main Video Embed */}
          <div className="max-w-4xl mx-auto">
            <FadeInOnScroll>
              <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden shadow-2xl">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/hNOHyNQx5rs"
                  title="Giovanni Tende - Video Installazioni"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </div>
            </FadeInOnScroll>

            {/* Channel Link */}
            <FadeInOnScroll delay={200}>
              <div className="mt-8 text-center">
                <a 
                  href="https://youtube.com/@tappezzeriagiovanni7149" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 text-gray-600 hover:text-red-600 transition-colors duration-300"
                >
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                  <span className="text-lg font-medium">@tappezzeriagiovanni7149</span>
                  <ExternalLink className="w-4 h-4" />
                </a>
                <p className="mt-2 text-sm text-gray-500">
                  Iscriviti al canale per rimanere aggiornato sui nuovi lavori
                </p>
              </div>
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* Photo Gallery Collage */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <SectionTitle
              label="Galleria Fotografica"
              title="Le nostre bioclimatiche in tutta Italia"
              description="Dalla Toscana alla Sardegna, portiamo eleganza e funzionalità in ogni angolo d'Italia."
            />
          </FadeInOnScroll>

          {/* Collage Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
            {/* Toscana Notte */}
            <FadeInOnScroll className="col-span-2 row-span-2">
              <div className="relative overflow-hidden rounded-lg shadow-xl group h-full">
                <img
                  src="https://customer-assets.emergentagent.com/job_tuscany-tende/artifacts/19exinuu_biocliamtica%20toscana%20notte.jpeg"
                  alt="Bioclimatica illuminata di notte in Toscana"
                  className="w-full h-full min-h-[400px] object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </FadeInOnScroll>

            {/* Azienda Vinicola */}
            <FadeInOnScroll delay={100}>
              <div className="relative overflow-hidden rounded-lg shadow-xl group">
                <img
                  src="https://customer-assets.emergentagent.com/job_tuscany-tende/artifacts/cnsdws3c_bioclima%20azienda%20vinicola.png"
                  alt="Bioclimatica presso azienda vinicola toscana"
                  className="w-full h-48 lg:h-56 object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </FadeInOnScroll>

            {/* Sardegna Tavolara */}
            <FadeInOnScroll delay={150}>
              <div className="relative overflow-hidden rounded-lg shadow-xl group">
                <img
                  src="https://customer-assets.emergentagent.com/job_tuscany-tende/artifacts/hzonqwij_bioclima%20sardegna%20tavolara.png"
                  alt="Bioclimatica in Sardegna con vista Tavolara"
                  className="w-full h-48 lg:h-56 object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </FadeInOnScroll>

            {/* Multishot Sardegna */}
            <FadeInOnScroll delay={200} className="col-span-2">
              <div className="relative overflow-hidden rounded-lg shadow-xl group">
                <img
                  src="https://customer-assets.emergentagent.com/job_tuscany-tende/artifacts/sern1x99_multishot%20saredagna.png"
                  alt="Bioclimatica in Sardegna - Collage"
                  className="w-full h-48 lg:h-56 object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* Why Our Work is Different - APPROFONDITO */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <SectionTitle
              label="La Differenza"
              title="Perché i nostri lavori sono diversi?"
              description="Non siamo rivenditori che subappaltano il montaggio. Siamo artigiani che producono e installano con le proprie mani."
            />
          </FadeInOnScroll>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {whyDifferent.map((item, index) => (
              <FadeInOnScroll key={index} delay={index * 100}>
                <div className="p-8 lg:p-10 bg-gray-50 rounded-lg hover:shadow-lg transition-shadow duration-300">
                  <div className="flex items-start gap-6">
                    <div className="w-16 h-16 rounded-full bg-sky-500 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-medium text-gray-900 mb-3">
                        {item.title}
                      </h3>
                      <p className="text-gray-600 font-light leading-relaxed mb-4">
                        {item.description}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {item.highlights.map((highlight, hIndex) => (
                          <span 
                            key={hIndex}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-sky-100 text-sky-700 text-sm rounded-full"
                          >
                            <CheckCircle className="w-3 h-3" />
                            {highlight}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </FadeInOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Summary Box */}
      <section className="py-16 bg-gradient-to-br from-sky-500 to-sky-600 text-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <Award className="w-16 h-16 mx-auto mb-6 opacity-90" />
            <h2 className="text-2xl md:text-3xl font-light mb-4">
              Una sola azienda. Una sola responsabilità.
            </h2>
            <p className="text-lg text-white/90 font-light mb-8 max-w-2xl mx-auto">
              Quando scegli Giovanni Tende, scegli un'azienda che si occupa di tutto: 
              progettazione, produzione, installazione e assistenza. 
              <strong> Non dovrai mai chiederti chi chiamare in caso di problemi.</strong>
            </p>
            <Link to="/preventivo">
              <Button className="bg-white text-sky-600 hover:bg-gray-100 px-8 py-4 text-lg rounded-none">
                Richiedi un Preventivo Gratuito
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection
        title="Vuoi vedere il tuo progetto realizzato?"
        description="Contattaci per una consulenza gratuita. Il prossimo video potrebbe essere il tuo!"
        variant="light"
      />
    </Layout>
  );
};

export default InstallationsPage;
